import {
  createContext, useCallback, useContext, useEffect, useMemo, useRef, useState
} from 'react';
import {
  api, describeError, setGuestMode,
  type ApiError, type Me, type PublicPlans, type SecretsRead, type SettingsRead, type SettingsWriteOk
} from '../lib/api.ts';
import type { SettingValue } from '../lib/settings-schema.ts';
import { materialize } from '../lib/settings-schema.ts';
import type { Lang } from '../lib/i18n.ts';
import {
  applyLook, loadLook, lookFromSettings, saveLook, settingsFromLook, type Look
} from '../lib/appearance.ts';
import { freeQuotas } from '../lib/guest.ts';
import { captureRef, localKV, runReferralBind, type RefBindState } from '../lib/referral-bind.ts';
import { rememberSignedIn } from '../lib/prerender-dom.ts';

/**
 * 全应用共享的那点状态。
 *
 * ════════════════════════════════════════════════════════
 * 刻意不引状态管理库。
 * ════════════════════════════════════════════════════════
 * 真正需要全局共享的只有四样：我是谁、我的设置、我的凭据掩码、外观。
 * 它们都是「进应用时拉一次，改完重拉」的形态 ——
 * 一个 context 加几个 useState 就够。
 *
 * 各页面自己的数据（钱包列表、成交流水）**不放这里**：
 * 放进来的话进任何一页都会触发全部请求，而且缓存失效要自己管。
 * 页面自己拉、自己管，离开就扔。
 *
 * ════════════════════════════════════════════════════════
 * 没登录的人有两种，处理完全不同
 * ════════════════════════════════════════════════════════
 *   **游客**（guest）      这次打开网页时就没登录。第一次来的人、没注册的人。
 *                          → 让他随便逛：每一页都能看、能改着玩，保存时再邀请登录。
 *   **会话丢了**（sessionLost） 本来登录着，用着用着服务器说会话过期了。
 *                          → 整屏换成登录框。**不能**悄悄变成游客 ——
 *                            那样他的设置会在眼前「变回默认值」，看起来像数据丢了。
 *
 * 以前只有一种：没登录 = 整屏登录框，什么都看不到。
 * 对第一次来的人来说，那等于在门口贴一张「先交出邮箱再说」。
 */

export interface AppState {
  /** 还在拉第一批数据 */
  booting: boolean;
  /** 游客：这次打开时就没登录。能逛所有页面，写操作要登录 */
  guest: boolean;
  /** 用着用着会话过期了。整屏登录框会自己显示。**不是游客** */
  sessionLost: boolean;
  /** 启动就失败了（服务器挂了 / 网络断了）。**不是没登录** */
  bootError: string | null;

  me: Me | null;
  /**
   * 设置（已经合并过默认值，所以每个键都有值）。
   * 游客的这一份是「默认值 + 他在这台电脑上选的外观和语言」。
   */
  settings: Readonly<Record<string, SettingValue>>;
  /** 乐观锁版本号 */
  version: number;
  /** 因为依赖没满足而当前不生效的键 */
  inactive: readonly string[];
  secrets: SecretsRead | null;
  /**
   * 当前生效的额度。登录了就是账号的；游客按免费版算（见 guest.ts 的 freeQuotas）。
   * undefined = 还不知道（游客的价目表还没拉回来），这时候不锁任何选项。
   */
  quotas: Readonly<Record<string, number>> | undefined;
  /** 价目表。游客模式下才会去拉；首页和账号页用 */
  plans: PublicPlans | null;

  /**
   * 这次打开有没有「绑邀请人」这件事发生，结果如何。null = 没有。
   *
   * 成功和失败都要有交代（账号页上那张「我的邀请码」卡片在说），
   * 但**失败绝不影响登录** —— 它只是这里的一个值，不会变成 bootError，
   * 也不会让任何一屏变成红色。见 lib/referral-bind.ts。
   */
  refBind: RefBindState | null;

  lang: Lang;
  look: Look;

  /** 重新拉设置和凭据。保存成功后调它 */
  reload: () => Promise<void>;
  /** 只重拉凭据。改完一条凭据后调 */
  reloadSecrets: () => Promise<void>;

  /**
   * 换界面语言。**立刻落库**，返回 null 表示成功。
   * 游客没有库可落，只存在这台电脑上（登录时会带进新账号，见 onSignedIn）。
   *
   * ════════════════════════════════════════════════════════
   * 这是全站唯一一个「单键即时保存」的设置，是有意的例外。
   * ════════════════════════════════════════════════════════
   * 其他 291 项都走「草稿 → 整页保存」（理由见 settings-schema.ts 的
   * `Draft`：乐观锁互相打架、审计被淹、抖动留下一半生效）。
   * 语言不适用那套理由，而且不即时落库会很怪：
   *   顶栏那个地球菜单在**每一页**都能点到，而多数页面根本没有保存按钮
   *   换语言的人要马上看到整个界面变过去
   *   它不是滑块，一天点不了几次，不存在「几十个请求」的问题
   *
   * 外观页里那个语言卡片走的仍然是草稿那条路（它就在保存按钮旁边）。
   * 两条路写的是同一个键，后写的赢 —— 这是设置本来的语义。
   */
  setLang: (lang: Lang) => Promise<ApiError | null>;

  /**
   * 外观预览。把一份「设置值 + 草稿改动」合成的外观写到 `<html>` 上，
   * **不落盘** —— 落盘只在保存成功之后。
   */
  previewLook: (settings: Readonly<Record<string, SettingValue>>) => void;
  /** 取消预览，回到服务器上的外观 */
  clearPreview: () => void;
  signOut: () => Promise<void>;
  /** 登录成功后调。会把全部数据重新拉一遍 */
  onSignedIn: () => Promise<void>;
}

const Ctx = createContext<AppState | null>(null);

export function useApp(): AppState {
  const v = useContext(Ctx);
  if (!v) throw new Error('useApp 必须在 <AppProvider> 里用');
  return v;
}

interface Loaded {
  error: ApiError | null;
  /** 和默认值不同的那些项（键 → 值）。登录时判断「这个账号自己设过语言没有」要用 */
  overrides: Readonly<Record<string, unknown>>;
  version: number;
}

export function AppProvider({ children }: { children: React.ReactNode }): React.ReactElement {
  /* 首屏先用缓存里的外观，避免闪一下 —— index.html 里那段脚本已经写过 <html>，
     这里只是把同一份值读进 React 状态 */
  const [look, setLook] = useState<Look>(() => loadLook());
  const [booting, setBooting] = useState(true);
  const [guest, setGuest] = useState(false);
  const [sessionLost, setSessionLost] = useState(false);
  const [bootError, setBootError] = useState<string | null>(null);
  const [me, setMe] = useState<Me | null>(null);
  const [settings, setSettings] = useState<Readonly<Record<string, SettingValue>>>({});
  const [version, setVersion] = useState(0);
  const [inactive, setInactive] = useState<readonly string[]>([]);
  const [secrets, setSecrets] = useState<SecretsRead | null>(null);
  const [plans, setPlans] = useState<PublicPlans | null>(null);
  const [refBind, setRefBind] = useState<RefBindState | null>(null);

  /* 服务器上那份外观（游客的话是这台电脑上那份）。取消预览时要能回到它 */
  const serverLook = useRef<Look>(look);
  /* 回调里要读「现在是不是游客」，用 ref 免得闭包拿到旧值 */
  const guestRef = useRef(false);

  const applyAndRemember = useCallback((next: Look) => {
    serverLook.current = next;
    setLook(next);
    applyLook(next);
    /* **只在这里写缓存** —— 它代表「服务器确认过的外观」（游客：他自己选定的）。
       预览中的值不写，否则改了没保存就刷新会先按预览画一遍、
       再被服务器的旧值改回去，用户看到莫名跳一下。 */
    saveLook(next);
  }, []);

  const loadSettings = useCallback(async (): Promise<Loaded> => {
    const r = await api.get<SettingsRead>('/settings');
    if (!r.ok) return { error: r.error, overrides: {}, version: 0 };
    /* 服务器只回「和默认值不同的部分」，这里合并成完整设置。
       合并逻辑用后端那一份（materialize），不自己写 —— 见 settings-schema.ts */
    const full = materialize(r.data.settings as Record<string, SettingValue>);
    setSettings(full);
    setVersion(r.data.version);
    setInactive(r.data.inactive);
    /* 外观**和语言**全部从这一份设置算出来 */
    applyAndRemember(lookFromSettings(full));
    return { error: null, overrides: r.data.overrides, version: r.data.version };
  }, [applyAndRemember]);

  const reloadSecrets = useCallback(async () => {
    if (guestRef.current) return;
    const r = await api.get<SecretsRead>('/secrets');
    if (r.ok) setSecrets(r.data);
    /* 凭据列表拉不到不该挡住整个应用 —— 它只影响那十二行的显示 */
  }, []);

  /**
   * 进入游客模式。
   *
   * 设置 = 默认值 + 这台电脑上的外观和语言。**不去拉任何要登录的接口** ——
   * api.ts 那一层也会拦，这里不发是为了不让控制台里堆一排 401。
   * 价目表是公开的，拉回来之后游客看到的配额锁按免费版算。
   */
  const enterGuest = useCallback(() => {
    setGuestMode(true);
    guestRef.current = true;
    const local = loadLook();
    serverLook.current = local;
    setLook(local);
    applyLook(local);
    setSettings({ ...materialize({}), ...settingsFromLook(local) });
    setVersion(0);
    setInactive([]);
    setSecrets(null);
    setMe(null);
    setGuest(true);
    setSessionLost(false);
    /* 下次打开首页时让预渲染的首页正常显示（见 lib/prerender-dom.ts） */
    rememberSignedIn(false);
    void api.get<PublicPlans>('/public/plans').then(r => {
      /* 拉不到就算了：只影响「游客模式下哪些选项显示成 VIP 专属」，
         不值得为它挡住整个应用或者弹错 */
      if (r.ok && guestRef.current) setPlans(r.data);
    });
  }, []);

  /**
   * 把落地页存下来的邀请码绑到这个账号上（`POST /api/referral/bind`）。
   *
   * ════════════════════════════════════════════════════════
   * **登录永远优先。** 这个函数不许影响启动流程的任何一步。
   * ════════════════════════════════════════════════════════
   * 返佣服务挂了、接口超时、返回的形状和我们想的不一样 —— 任何一种都只是
   * 「这次没绑上，下次打开再试」，不得让人登不进来，也不得冒出一屏红色错误。
   * 所以它：不 await（启动不等它）、不写 bootError、整个包在 try 里。
   *
   * 该不该绑、过期了没、失败了要不要再试，全在 lib/referral-bind.ts 的纯函数里。
   */
  const bindReferral = useCallback(async (who: Me): Promise<void> => {
    try {
      const st = await runReferralBind(
        {
          /* 用通用的 api.post，不在 api.ts 里加包装函数 —— 这一条路径只有这里走 */
          post: (code, capturedAt) => api.post('/referral/bind', { code, ...(capturedAt !== undefined ? { capturedAt } : {}) }),
          storage: localKV(),
          now: Date.now()
        },
        {
          userId: who.user?.id ?? null,
          accountCreatedAt: who.user?.createdAt ?? null,
          /* 后端已经绑过的就不去撞它（撞了只会拿到一个 409） */
          alreadyBound: (who.profile?.referredBy ?? null) !== null
        }
      );
      if (st !== null) setRefBind(st);
    } catch {
      /* 走到这里说明出了预料之外的错（比如返回形状变了）。
         邀请关系记不上是一笔返佣的事，而抛出去会卡住整个启动 —— 孰轻孰重很清楚。
         码还在 localStorage 里，下次打开会再试一次 */
    }
  }, []);

  const boot = useCallback(async (opts: { carryLang?: Lang | null } = {}) => {
    setBooting(true);
    setBootError(null);
    /* **先把游客拦截关掉。** 这一步的目的就是「问服务器我到底登录了没有」——
       游客刚登录完、再走一遍这里时，拦截还开着，`/me` 会被拦成「要先登录」，
       于是刚登录成功的人看到一屏「连不上服务器」。真浏览器里走一遍才抓到的。
       问完没登录，enterGuest 会再把它打开 */
    setGuestMode(false);
    guestRef.current = false;
    const meRes = await api.get<Me>('/me');
    if (!meRes.ok) {
      if (meRes.error.kind === 'unauthorized') {
        enterGuest();
        setBooting(false);
        return;
      }
      /* 区分「没登录」和「服务器有问题」很重要：
         前者该让他进来逛，后者显示登录页会让用户以为自己被登出了，
         然后反复登录反复失败 */
      setBootError(describeError(meRes.error));
      setBooting(false);
      return;
    }
    setGuestMode(false);
    guestRef.current = false;
    setGuest(false);
    setSessionLost(false);
    setMe(meRes.data);
    /* 下次打开首页时不先闪一下给游客看的首页（见 lib/prerender-dom.ts） */
    rememberSignedIn(true);

    const loaded = await loadSettings();
    if (loaded.error) {
      setBootError(describeError(loaded.error));
      setBooting(false);
      return;
    }

    /**
     * 游客时选的语言带进账号 —— **只在这个账号自己没设过语言时。**
     *
     * 一个英文用户在首页切到 English、逛了一圈、注册，
     * 登录那一刻整个界面变回简体中文，他得再去找一次地球菜单。
     * 这是他对这个产品的第一印象，不该是这样。
     *
     * 账号里已经设过语言（老用户换了台电脑）就以账号为准：
     * 那是他明确选过的，比这一次随手点的更算数。
     * 写失败不挡登录 —— 语言是小事，顶栏点一下就能改。
     */
    const wanted = opts.carryLang ?? null;
    /* 用 hasOwnProperty 而不是 `in`：overrides 是服务器给的普通对象，
       `'lang' in {}` 在原型链上碰不到东西，但写法上要表达的是「这个账号自己存过」 */
    const accountSetLang = Object.prototype.hasOwnProperty.call(loaded.overrides ?? {}, 'lang');
    if (wanted !== null && !accountSetLang && wanted !== serverLook.current.lang) {
      const w = await api.patch<SettingsWriteOk>('/settings', { version: loaded.version, patch: { lang: wanted } });
      if (w.ok) await loadSettings();
    }

    await reloadSecrets();
    /**
     * 价目表**登录之后也要读**。
     *
     * 这里原来写的是 `setPlans(null)` —— 想法是「登录之后按自己的配额算，
     * 不用价目表」。但价目表里还有价格，而订阅页要用它：
     * 结果是登录用户打开订阅与付款，「免费版和 VIP」那张卡永远停在
     * 「正在读价目表…」，套餐按钮上也没有价钱。
     * 而这件事以前看不出来，因为登录之后没有一个页面显示价目表。
     *
     * 它是公开的、带五分钟缓存的接口，登录用户多读一次可以忽略。
     * 拉不到就留 null，页面上如实显示「正在读」。
     */
    const plansRes = await api.get<PublicPlans>('/public/plans');
    setPlans(plansRes.ok ? plansRes.data : null);

    /**
     * 邀请人绑定放在最后，而且**不 await**。
     *
     * 放最后：前面每一步都是「他能不能用这个产品」，这一步只是「这笔返佣记给谁」。
     * 不等它：这是一个网络请求，慢的时候 20 秒才超时 ——
     * 让刚注册的人多看 20 秒的「读取中…」，为的是一件他根本不知道正在发生的事。
     *
     * 它自己会把结果写进 refBind（账号页上交代），失败也只是写一个状态。
     */
    void bindReferral(meRes.data);
    setBooting(false);
  }, [enterGuest, loadSettings, reloadSecrets, bindReferral]);

  /**
   * 启动流程里**任何一处意外抛错都不许把人卡在转圈里。**
   *
   * 踩过一次：返回形状和类型声明对不上，某一行抛了 TypeError，
   * boot 停在半路、`booting` 永远是 true —— 刚登录的人面对一个「读取中…」
   * 永远转下去，没有任何提示，也没有重试按钮。
   * 兜住之后至少是一屏「出了什么错 + 重新试一次」。
   */
  const safeBoot = useCallback(async (opts: { carryLang?: Lang | null } = {}) => {
    try {
      await boot(opts);
    } catch (e) {
      setBootError('启动时出了意外：' + (e instanceof Error ? e.message : String(e)));
      setBooting(false);
    }
  }, [boot]);

  const booted = useRef(false);
  useEffect(() => {
    if (booted.current) return;
    booted.current = true;
    /**
     * **先把链接上的 `?ref=` 存下来，再去问「我登录了没有」。**
     *
     * 顺序是刻意的：存码只碰 localStorage、不发请求（带着邀请链接进来的人
     * 就是游客，api 那一层会把写请求直接挂掉），所以它可以、也应该在
     * 任何网络动作之前完成 —— 这样就算接口全挂了、启动停在错误屏上，
     * 那个码也已经存住了，他下次进来照样能绑上。
     *
     * 这里读的是 `window.location.search` 而不是路由的 location：
     * 本组件在路由外层也要能工作，而且 Google 登录跳回来时是**一次全新的
     * 文档加载**，那一刻路由还没挂好，但 `window.location` 一定是对的。
     */
    try {
      captureRef(localKV(), window.location.search, Date.now());
    } catch {
      /* 存不下只是这笔邀请关系记不住，绝不能因此挡住启动 */
    }
    void safeBoot();
  }, [safeBoot]);

  const reload = useCallback(async () => {
    if (guestRef.current) return;
    const loaded = await loadSettings();
    /* 用着用着会话没了。**整屏登录框，不是变成游客** —— 见文件开头 */
    if (loaded.error && loaded.error.kind === 'unauthorized') setSessionLost(true);
  }, [loadSettings]);

  const setLang = useCallback(async (next: Lang): Promise<ApiError | null> => {
    if (next === serverLook.current.lang) return null;
    if (guestRef.current) {
      /* 游客：只存这台电脑。设置里的 lang 也跟着改，外观页才显示得对 */
      applyAndRemember({ ...serverLook.current, lang: next });
      setSettings(s => ({ ...s, lang: next }));
      return null;
    }
    const r = await api.patch<SettingsWriteOk>('/settings', {
      version,
      /* 只带 lang 一个键。**不要顺手把整份设置发过去** ——
         那会把别处（另一台设备、另一个标签页）的改动一起盖掉 */
      patch: { lang: next }
    });
    if (!r.ok) {
      /* 撞版本号的话先把最新的拉回来，用户再点一次就成 ——
         这里刻意不自动重试：自动重试要先想清楚「别人改了什么」，
         而为一个点两次就能解决的罕见情况加那层逻辑不值得 */
      if (r.error.kind === 'conflict') await loadSettings();
      return r.error;
    }
    await loadSettings();
    return null;
  }, [version, loadSettings, applyAndRemember]);

  const previewLook = useCallback((draftSettings: Readonly<Record<string, SettingValue>>) => {
    /* 草稿里已经包含了配色、强调色、语言的改动（它们现在是普通设置项），
       所以一份草稿就能算出完整的预览外观 */
    const next = lookFromSettings(draftSettings);
    /* 只改 DOM 和 React 状态，**不动 serverLook、不写缓存** */
    setLook(next);
    applyLook(next);
  }, []);

  const clearPreview = useCallback(() => {
    setLook(serverLook.current);
    applyLook(serverLook.current);
  }, []);

  const signOut = useCallback(async () => {
    await api.post('/auth/sign-out');
    /* 整页刷新而不是改状态：登出要确保内存里不留任何上一个账号的数据。
       手工清状态一定会漏 —— 每加一个页面缓存就多一个可能漏的地方。 */
    window.location.href = '/';
  }, []);

  const onSignedIn = useCallback(async () => {
    /* 先记下游客时的语言，boot 会用账号的设置把它盖掉 */
    const carryLang = guestRef.current ? serverLook.current.lang : null;
    booted.current = true;
    await safeBoot({ carryLang });
  }, [safeBoot]);

  /* 记忆住，不然每次渲染都是一个新对象，所有读 context 的组件跟着白渲染一遍 */
  const quotas = useMemo(
    () => (me ? me.entitlements.quotas : guest ? freeQuotas(plans) : undefined),
    [me, guest, plans]
  );

  const value = useMemo<AppState>(() => ({
    booting, guest, sessionLost, bootError, me, settings, version, inactive, secrets, quotas, plans, refBind,
    lang: look.lang, look,
    reload, reloadSecrets, setLang, previewLook, clearPreview, signOut, onSignedIn
  }), [
    booting, guest, sessionLost, bootError, me, settings, version, inactive, secrets, quotas, plans, refBind, look,
    reload, reloadSecrets, setLang, previewLook, clearPreview, signOut, onSignedIn
  ]);

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

/**
 * **只给构建时的预渲染用**（src/prerender/entry.tsx）：用一份写死的状态（游客、简体中文、
 * 构建时读到的价目表）把公开页面渲染成 HTML。不发请求、不碰 localStorage。
 * 浏览器里永远走上面的 AppProvider。
 */
export function StaticAppProvider({ value, children }: { value: AppState; children: React.ReactNode }): React.ReactElement {
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}
